import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Scales,
  CircleNotch,
  CheckCircle,
  XCircle,
  ArrowRight,
  Copy,
  Check,
} from '@phosphor-icons/react';
import { getSessionStatus, type SessionStatus, type ProvisioningStep } from '@/lib/onboardingApi';

const STEP_LABELS: Record<string, string> = {
  create_chambers: 'Creating workspace',
  create_admin_user: 'Creating administrator account',
  create_barrister_profile: 'Setting up barrister profile',
  seed_roles: 'Configuring roles and permissions',
  seed_practice_areas: 'Seeding practice areas',
  create_subscription: 'Activating subscription',
  finalize: 'Finalising workspace',
};

function StepRow({ step }: { step: ProvisioningStep }) {
  const label = STEP_LABELS[step.step] ?? step.step;
  return (
    <div className="flex items-center gap-3 py-2">
      {step.status === 'ok' ? (
        <CheckCircle className="h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" weight="fill" />
      ) : (
        <XCircle className="h-4 w-4 flex-shrink-0 text-red-500" weight="fill" />
      )}
      <span className={`text-sm ${step.status === 'ok' ? 'text-[#3d3b35]' : 'text-red-700 dark:text-red-300'}`}>{label}</span>
      {step.status === 'failed' && step.message && (
        <span className="text-xs text-red-500">— {step.message}</span>
      )}
    </div>
  );
}

export default function OnboardingProvisioningPage() {
  const { token } = useParams<{ token: string }>();
  const [session, setSession] = useState<SessionStatus | null>(null);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const isDone = session?.status === 'provisioned' || session?.status === 'activated';
  const isFailed = session?.status === 'failed';
  const isRunning = !isDone && !isFailed;

  useEffect(() => {
    if (!token) return;
    let active = true;

    async function poll() {
      try {
        const s = await getSessionStatus(token!);
        if (!active) return;
        setSession(s);
        if (s.status !== 'provisioned' && s.status !== 'activated' && s.status !== 'failed') {
          setTimeout(poll, 2500);
        }
      } catch {
        if (!active) return;
        setError('Could not check provisioning status.');
      }
    }

    poll();
    return () => { active = false; };
  }, [token]);

  function copyActivationUrl() {
    const url = session?.activationUrl;
    if (!url) return;
    void navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  // Extract activation token from URL if present
  const activationUrl = session?.activationUrl;
  const activationToken = activationUrl ? activationUrl.split('/activate/')[1] : null;

  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <header className="border-b border-[#e5e3de] bg-white px-8 py-4">
        <div className="mx-auto max-w-6xl">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-[#1a2f4e]">
              <Scales className="h-4 w-4 text-white" weight="duotone" />
            </div>
            <span className="text-sm font-semibold tracking-tight text-[#1a1915]">Jurivoxa</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-6 py-16 text-center">
        {/* Running */}
        {isRunning && (
          <>
            <div className="mb-6 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#1a2f4e]/10">
                <CircleNotch className="h-8 w-8 animate-spin text-[#1a2f4e]" />
              </div>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-[#1a1915]">Preparing your workspace</h1>
            <p className="mb-8 text-sm text-[#6b6961]">This takes about 10 seconds. Do not close this page.</p>
          </>
        )}

        {/* Done */}
        {isDone && (
          <>
            <div className="mb-6 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" weight="fill" />
              </div>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-[#1a1915]">Your workspace is ready</h1>
            <p className="mb-8 text-sm text-[#6b6961]">
              Activate your account by setting your password. The link is valid for 7 days.
            </p>
          </>
        )}

        {/* Failed */}
        {isFailed && (
          <>
            <div className="mb-6 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
                <XCircle className="h-8 w-8 text-red-500" weight="fill" />
              </div>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-[#1a1915]">Provisioning failed</h1>
            <p className="mb-6 text-sm text-[#6b6961]">
              Something went wrong setting up your workspace. Our team has been notified.
            </p>
            {/* UX-2: the raw provisioning errorMessage is NOT shown to the client — it's a
                developer/internal string. It remains captured in the provisioning record + server
                logs for diagnostics; the client sees only a safe message + a support contact. */}
            <p className="text-sm text-[#6b6961]">
              Please contact <a href="mailto:support@jurivoxa.com" className="text-[#1a2f4e] underline">support@jurivoxa.com</a> with your email address.
            </p>
          </>
        )}

        {/* Network error */}
        {error && !session && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}

        {/* Provisioning steps log */}
        {session?.provisioning?.steps && session.provisioning.steps.length > 0 && (
          <div className="mt-6 rounded-lg border border-[#e5e3de] bg-white p-4 text-left">
            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-[#9c9990]">Provisioning log</p>
            {session.provisioning.steps.map((step) => (
              <StepRow key={step.step} step={step} />
            ))}
          </div>
        )}

        {/* Activation CTA */}
        {isDone && activationToken && (
          <div className="mt-8 space-y-3">
            <Link
              to={`/activate/${activationToken}`}
              className="flex w-full items-center justify-center gap-2 rounded bg-[#1a2f4e] py-3 text-sm font-medium text-white hover:bg-[#243d61]"
            >
              Set your password and sign in <ArrowRight className="h-4 w-4" />
            </Link>

            <div className="flex items-center gap-2 rounded border border-[#e5e3de] bg-[#faf9f6] px-3 py-2">
              <span className="flex-1 truncate text-left text-xs text-[#6b6961] font-mono">
                {activationUrl}
              </span>
              <button
                onClick={copyActivationUrl}
                className="flex items-center gap-1 text-xs text-[#1a2f4e] hover:underline"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </div>
            <p className="text-xs text-[#9c9990]">Save this activation link — it is valid for 7 days.</p>
          </div>
        )}
      </main>
    </div>
  );
}
